<template>
  <div
    data-component-group="contract-form"
    data-component="edit"
    class="elevation-1"
  >
    <div class="component-body">
      <v-progress-linear
        indeterminate
        color="primary"
        :active="isLoading"
      />
      <ContractForm
        :contract="contract"
        confirm-trigger-label="Update contract"
        :is-disabled="!canEditDetails"
        :is-loading="isLoading"
        :on-submit="onSubmit"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Details"
            :has-trigger="$ability.can('update', 'contract')"
            :trigger-label="!canEditDetails ? 'Edit details' : 'Close'"
            @toggle="toggleEditDetails"
          />
        </template>
      </ContractForm>

      <Can
        action="owner"
        subject="contract"
      >
        <BillingForm
          v-if="isCpcContract"
          :contract="contract"
          :is-disabled="!canEditBilling"
          :on-submit="onUpdateBilling"
        >
          <template v-slot:beforeForm>
            <layout-section-header
              :has-trigger="false"
              title="Billing account"
            />
            <!-- TODO: delete if not used -->
            <!-- <layout-section-header
              title="Billing account"
              :trigger-label="!canEditBilling ? 'Edit' : 'Close'"
              @toggle="toggleBilling"
            /> -->
          </template>
        </BillingForm>
      </Can>

      <Can
        action="capping-discount-delete"
        subject="contract"
      >
        <CappingForm
          v-if="isCpcContract"
          :contract="contract"
          :is-disabled="!canEditCapping"
        >
          <template v-slot:beforeForm>
            <layout-section-header
              title="Capping discount"
              :trigger-label="!canEditCapping ? $t('edit') : $t('close')"
              @toggle="toggleCapping"
            />
          </template>
        </CappingForm>
      </Can>

      <Can
        action="price-delete"
        subject="contract"
      >
        <PricesForm
          v-if="isCpcContract"
          :contract="contract"
          :is-disabled="!canEditPrices"
        >
          <template v-slot:beforeForm>
            <layout-section-header
              title="Contract prices"
              :trigger-label="!canEditPrices ? $t('edit') : $t('close')"
              @toggle="togglePrice"
            />
          </template>
        </PricesForm>
      </Can>

      <Can
        action="domain-delete"
        subject="contract"
      >
        <TrackingForm
          v-if="isCpcContract"
          :domain="domain"
          :is-disabled="!canEditTracking"
          :is-loading="isLoadingTracking"
          :on-submit="onUpdateDomain"
        >
          <template v-slot:beforeForm>
            <layout-section-header
              title="Tracking setup"
              :has-trigger="!isActivated"
              :trigger-label="!canEditTracking ? $t('edit') : $t('close')"
              @toggle="toggleTracking"
            />
          </template>
        </TrackingForm>
      </Can>
    </div>
  </div>
</template>

<script>
  import ContractForm from './AddNew';
  import BillingForm from './Billing';
  import TrackingForm from './Tracking';
  import PricesForm from './Prices';
  import CappingForm from './Capping';
  import { isObj, isStr } from '../../../utils';
  import defaultPagination from '../../../config/pagination';

  // TODO: Translate component

  export default {
    components: {
      ContractForm,
      BillingForm,
      TrackingForm,
      CappingForm,
      PricesForm
    },
    props: {
      contract: {
        type: Object,
        required: true
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      onCancel: {
        type: Function,
        default: undefined
      },
      onSubmit: {
        type: Function,
        default: undefined
      },
    },
    data() {
      return {
        pagination: {
          ...defaultPagination
        },
        canEditDetails: false,
        canEditBilling: false,
        canEditTracking: false,
        canEditPrices: false,
        canEditCapping: false,
        isLoadingTracking: false
      };
    },
    computed: {
      billingAccount() {
        return this.contract.account;
      },
      domain() {
        const { domain } = this.contract;
        return isObj(domain) ? {
          uri: domain.url,
          keywords: domain.keywords
        } : domain;
      },
      prices() {
        return [];
      },
      isCpcContract() {
        const { type } = this.contract;
        return isObj(type)
          ? type.id === 1
          : false;
      },
      isActivated() {
        return this.contract.activated === 1 && !this.$ability.can('domain-store-sa', 'contract');
      }
    },
    created() {
      if (this.contract?.id) {
        this.getPrices(this.contract);
      }
    },
    watch: {
      contract(nextContract, prevContract) {
        if (isObj(nextContract)) {
          this.canEditDetails = false;
          this.canEditBilling = false;
          this.canEditTracking = false;
          this.canEditCapping = false;
          this.pagination = defaultPagination;

          if (nextContract.id !== prevContract.id) {
            this.canEditPrices = false;

            this.getPrices(nextContract);
          }
        }
      }
    },
    methods: {
      toggleEditDetails() {
        this.canEditDetails = !this.canEditDetails;
      },
      toggleBilling() {
        this.canEditBilling = !this.canEditBilling;
      },
      toggleTracking() {
        this.canEditTracking = !this.canEditTracking;
      },
      togglePrice() {
        this.canEditPrices = !this.canEditPrices;
      },
      toggleCapping() {
        this.canEditCapping = !this.canEditCapping;
      },
      onUpdateBilling(billingAccount) {
        const { contract } = this;
        const query = {
          id: contract.id,
          billing_account: billingAccount.id
        };

        return this.$store.dispatch('contracts/setBilling', query);
      },
      onUpdateDomain(domain) {
        this.isLoadingTracking = true;
        const { contract } = this;
        const query = {
          id: contract.id,
          tracking_url: domain.uri,
          seller_id: domain.seller_id,
          keywords: isStr(domain.keywords) && domain.keywords !== ''
            ? domain.keywords.split(',').map(item => item.trim())
            : []
        };

        return this.$store.dispatch('contracts/setDomain', query).finally(() => {
            this.isLoadingTracking = false;
          })
      },
      getPrices(contract) {
        const query = {
          id: contract.id
        };

        return this.$store.dispatch('contracts/getPrices', query);
      }
    },
  };
</script>
